/**
 * Brand token generator — spawns a small internal agent (Haiku → Codex mini →
 * OpenCode nano fallback) that fills the design tokens on the colors page from
 * the brand palette.
 *
 * Modeled directly on `quest-metadata-generator.ts` — same internal-agent
 * pattern that the queen-bee orchestration and quest auto-naming use, so this
 * doesn't add a parallel AI pipeline. Auth, observability, and the
 * "promote-to-visible-tab" affordance all come for free from the existing
 * agent system.
 *
 * Two modes:
 *   - "auto"   — the agent derives token values from the dynamic palette
 *                alone (labels, hex/gradient values, role tags, weights).
 *                Palette entries are read-only in this mode — only the
 *                semantic `token.*` slots are filled.
 *   - "prompt" — same, plus an extra freeform user direction. In this mode
 *                the agent may also rewrite unlocked palette entries
 *                (shifting hues, swapping a gradient) when the user's
 *                direction warrants it.
 *
 * The palette is a dynamic list — entries have kebab-case slugs
 * (`color.duvel-red`), human labels ("Duvel Red"), a `$type` of color or
 * gradient, and Appostle extensions carrying `role` and `weight`. The prompt
 * enumerates them generically; the agent never sees the legacy fixed
 * six-slot model ("whites" / "blacks" / "accent").
 *
 * Locked tokens are sacred: the daemon reads them from the file before
 * spawning the agent, tells the agent to skip them, and additionally drops
 * any locked-key entries from the agent's response defensively. The user's
 * locks are never overwritten under any failure mode.
 *
 * The prompt itself is not persisted. The resolved hex values + locks ARE
 * the brand contract — agents reading the brand later only see the result.
 */

import fs from "node:fs/promises";

import { z } from "zod";
import type { Logger } from "pino";

import type { AgentManager } from "../agent/agent-manager.js";
import {
  DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
  StructuredAgentFallbackError,
  StructuredAgentResponseError,
  generateStructuredAgentResponseWithFallback,
} from "../agent/agent-response-loop.js";
import type { BrandVariable } from "../brands/scanner.js";
import { listBrands } from "../brands/scanner.js";
import { tokensJsonPath } from "../brands/tokens-adapter.js";
import { writeBrandTokens } from "../brands/tokens-writer.js";

/**
 * Palette `role` is stored in `$extensions.ohlord.appostle.role` in
 * tokens.json but isn't surfaced through the BrandVariable model yet, so we
 * read it directly here. Returns a slug→role map for `color.<slug>` entries.
 * Best-effort: missing file or malformed JSON yields an empty map and the
 * prompt simply omits role tags.
 */
const ROLE_VALUES = ["primary", "secondary", "accent", "neutral"] as const;
type PaletteRole = (typeof ROLE_VALUES)[number];

async function readPaletteRoles(workspaceRoot: string): Promise<Map<string, PaletteRole>> {
  const roles = new Map<string, PaletteRole>();
  try {
    const text = await fs.readFile(tokensJsonPath(workspaceRoot), "utf8");
    const root = JSON.parse(text) as Record<string, unknown>;
    const color = root.color;
    if (!color || typeof color !== "object" || Array.isArray(color)) return roles;
    for (const [slug, node] of Object.entries(color as Record<string, unknown>)) {
      if (!node || typeof node !== "object" || Array.isArray(node)) continue;
      const extensions = (node as Record<string, unknown>).$extensions as
        | { ohlord?: { appostle?: { role?: unknown } } }
        | undefined;
      const candidate = extensions?.ohlord?.appostle?.role;
      if (typeof candidate === "string" && (ROLE_VALUES as readonly string[]).includes(candidate)) {
        roles.set(`color.${slug}`, candidate as PaletteRole);
      }
    }
  } catch {
    /* best effort */
  }
  return roles;
}

export type BrandTokenGenerationMode = "auto" | "prompt";

export interface BrandTokenGenerationOptions {
  agentManager: AgentManager;
  workspaceRoot: string;
  brandPath: string;
  mode: BrandTokenGenerationMode;
  /** User's freeform direction. Required when `mode === "prompt"`. */
  prompt?: string;
  logger: Logger;
  /** auth-server user-id of the caller — stamped on the internal agent. */
  ownerUserId: string;
}

export interface BrandTokenGenerationResult {
  /** Number of tokens written into the file. */
  generatedCount: number;
}

const HEX6 = /^#[0-9a-f]{6}$/i;
const CSS_GRADIENT = /^(linear|radial|conic)-gradient\(/i;

/**
 * The adapter emits hyphenated keys for the colors section to match the
 * preview component's filter patterns (e.g. `token.bg-base` for the
 * `"token.bg-"` prefix filter). The writer uses dot-path navigation on the
 * tokens.json tree, so these keys need translating back before write.
 *
 * Pattern: `token.{subkey}-{child}` → `token.{subkey}.{child}`
 * Works for all color-section sub-keys (bg, fg, accent, border, status).
 */
const TOKEN_COLOR_SUBKEYS = ["bg", "fg", "accent", "border", "status"];

function adapterKeyToTokensJsonPath(key: string): string {
  for (const subkey of TOKEN_COLOR_SUBKEYS) {
    const prefix = `token.${subkey}-`;
    if (key.startsWith(prefix)) {
      return `token.${subkey}.${key.slice(prefix.length)}`;
    }
  }
  return key;
}

/**
 * `accent-on-dark` is derived deterministically from `accent-base` and
 * `bg-inverted` after the LLM finishes. The brand contract guarantees this
 * value reads at WCAG AA (>= 4.5:1) on the inverted surface, so builders can
 * apply accent text on dark heroes without per-build judgment calls. The LLM
 * never fills this token; we always compute it.
 */
const DERIVED_ACCENT_ON_DARK = "token.accent-on-dark";
const WCAG_AA_TARGET = 4.5;

interface RGB {
  r: number;
  g: number;
  b: number;
}
interface HSL {
  h: number;
  s: number;
  l: number;
}

function hexToRgb(hex: string): RGB {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
}

function rgbToHex({ r, g, b }: RGB): string {
  const toHex = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl({ r, g, b }: RGB): HSL {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;
  let s = 0;
  let h = 0;
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / delta + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / delta + 2) * 60;
    else h = ((r - g) / delta + 4) * 60;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }: HSL): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = l - c / 2;
  return { r: r1 + m, g: g1 + m, b: b1 + m };
}

function relativeLuminance({ r, g, b }: RGB): number {
  const lin = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrastRatio(hexA: string, hexB: string): number {
  const lA = relativeLuminance(hexToRgb(hexA));
  const lB = relativeLuminance(hexToRgb(hexB));
  const lighter = Math.max(lA, lB);
  const darker = Math.min(lA, lB);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Derive `accent-on-dark` from the brand's accent and inverted background.
 *
 * Strategy:
 *   1. If accent already reads at >= 4.5:1 on bg-inverted, return it.
 *   2. Otherwise step lightness up in 5% increments along the same hue and
 *      saturation. First step that passes is the answer.
 *   3. Cap lightness at accent-subtle's lightness so we never wash past the
 *      already-declared subtle variant.
 *   4. If even the cap fails, fall back to accent-subtle (or accent if there
 *      is none).
 */
function deriveAccentOnDark(opts: {
  accent: string;
  bgInverted: string;
  accentSubtle?: string;
}): string {
  if (contrastRatio(opts.accent, opts.bgInverted) >= WCAG_AA_TARGET) {
    return opts.accent.toLowerCase();
  }
  const baseHsl = rgbToHsl(hexToRgb(opts.accent));
  const subtleL = opts.accentSubtle ? rgbToHsl(hexToRgb(opts.accentSubtle)).l : 0.95;
  const maxL = Math.min(0.97, Math.max(baseHsl.l + 0.05, subtleL));
  let l = baseHsl.l;
  while (l < maxL) {
    l = Math.min(l + 0.05, maxL);
    const candidate = rgbToHex(hslToRgb({ h: baseHsl.h, s: baseHsl.s, l }));
    if (contrastRatio(candidate, opts.bgInverted) >= WCAG_AA_TARGET) {
      return candidate;
    }
  }
  return (opts.accentSubtle ?? opts.accent).toLowerCase();
}

const TokensResponseSchema = z.object({
  tokens: z.record(z.string(), z.string()),
});

/**
 * Build the agent prompt.
 *
 * The prompt is intentionally explicit about the role of each token (via the
 * key prefix glossary) so the agent can derive values without inventing
 * tokens or producing values for tokens that aren't in the schema.
 */
function buildPrompt(args: {
  paletteVars: BrandVariable[];
  unlockedTokens: BrandVariable[];
  lockedTokens: BrandVariable[];
  unlockedGradients: BrandVariable[];
  lockedGradients: BrandVariable[];
  unlockedPaletteColors: BrandVariable[];
  unlockedPaletteGradients: BrandVariable[];
  paletteRoles: Map<string, PaletteRole>;
  mode: BrandTokenGenerationMode;
  userPrompt?: string;
}): string {
  // The palette is a dynamic, user-authored list — every entry is one of the
  // brand's identity colours or gradients, keyed by a kebab-case slug
  // (`color.duvel-red`) and tagged with an evocative label ("Duvel Red"),
  // an optional role, and a weight. The agent reads the whole list
  // generically; the legacy "whites/blacks/accent" slot names are gone.
  //
  // Split the `color.*` slice into the entries themselves (colour + gradient
  // primitives) and the metadata sidecars (`.weight`). Each entry line is
  // enriched inline with its metadata so the agent reads the palette as one
  // coherent picture instead of disconnected rows.
  const paletteEntries = args.paletteVars.filter(
    (v) =>
      (v.type === "color" || v.type === "gradient") &&
      v.key.startsWith("color.") &&
      !v.key.endsWith(".weight"),
  );
  const weightByKey = new Map<string, string>();
  for (const v of args.paletteVars) {
    if (v.key.endsWith(".weight")) {
      weightByKey.set(v.key.replace(/\.weight$/, ""), (v.value || "").trim());
    }
  }

  const palette = paletteEntries
    .map((v) => {
      const enabled = v.enabled !== false;
      const role = args.paletteRoles.get(v.key);
      const weight = weightByKey.get(v.key) ?? "";
      const flags: string[] = [];
      flags.push(v.type === "gradient" ? "gradient" : "color");
      if (role) flags.push(`role ${role}`);
      if (!enabled) {
        flags.push("OFF — disabled by the user, NOT part of the active palette");
      } else {
        if (weight.length > 0) flags.push(`weight ${weight}%`);
      }
      const label = v.label || v.key.split(".").slice(1).join(".") || v.key;
      const value = v.value || "(unset)";
      return `- ${v.key} ("${label}", ${value}) | ${flags.join(" | ")}`;
    })
    .join("\n");

  const hasOff = paletteEntries.some((v) => v.enabled === false);
  const hasWeights = paletteEntries.some((v) => {
    const w = weightByKey.get(v.key);
    return v.enabled !== false && w && w.length > 0;
  });
  const hasRoles = paletteEntries.some((v) => args.paletteRoles.has(v.key));

  const metadataLegend: string[] = [];
  if (hasRoles || hasOff || hasWeights) {
    metadataLegend.push("", "Palette metadata reference:");
    if (hasRoles) {
      metadataLegend.push(
        "- role — what the user intends this entry for in the brand system:",
        "    primary   = the brand's signature colour, the one most associated with",
        "                the identity; drives the dominant accent treatment.",
        "    secondary = supports primary; second-most prominent identity colour.",
        "    accent    = sparingly-used pop colour for highlights and callouts.",
        "    neutral   = backgrounds, text, and structural surfaces — quiet by design.",
        "  An entry without a role tag carries no explicit semantic intent; lean on",
        "  the label, weight, and value to judge how to use it.",
      );
    }
    if (hasOff) {
      metadataLegend.push(
        "- OFF — the user has disabled this entry. Treat it as if it isn't in the",
        "  palette: do NOT use its value in any generated token, do not derive",
        "  shades from it, do not reference it. Pretend it does not exist.",
      );
    }
    if (hasWeights) {
      metadataLegend.push(
        "- weight N% — that entry's share of the brand's visual presence across",
        "  the active palette (sums to ~100 across enabled entries). Higher weight",
        "  = more dominant. The highest-weight enabled entry should drive primary",
        "  surfaces / dominant accents; mid-weight entries support; low-weight",
        "  entries appear sparingly (subtle accents, hover/active states only).",
      );
    }
  }

  const tokensToFill = args.unlockedTokens.map((v) => `- ${v.key}`).join("\n");

  const gradientSection =
    args.unlockedGradients.length > 0
      ? [
          "",
          "Gradient tokens to fill (values must be valid CSS gradient strings,",
          "e.g. linear-gradient(135deg, #c52669 0%, #ff6b35 100%)):",
          ...args.unlockedGradients.map((v) => `- ${v.key}`),
          "",
          "Gradient key glossary:",
          "- gradient.primary — the brand's signature gradient, built from the",
          "  most prominent palette entries (typically primary + secondary roles,",
          "  or the two highest-weight enabled entries when roles aren't set).",
          "  Used for hero backgrounds, feature highlights, and primary decorative",
          "  surfaces.",
          "- gradient.accent  — a secondary gradient for interactive states,",
          "  hover effects, or accent surfaces. Distinct from primary but",
          "  harmonious with the palette.",
          "- gradient.subtle  — a very soft, low-contrast gradient for subtle",
          "  background washes — card backgrounds, section tints. Should feel",
          "  almost invisible; same hue family as bg-base with minimal shift.",
          "",
          "Include gradient values in the same JSON under the 'tokens' key,",
          "alongside the hex tokens. Gradient values are CSS gradient strings.",
        ].join("\n")
      : "";

  const lockedGradientSection =
    args.lockedGradients.length > 0
      ? [
          "",
          "Locked gradients — DO NOT include these in your output:",
          ...args.lockedGradients.map((v) => `- ${v.key}: ${v.value}`),
        ].join("\n")
      : "";

  const lockedSection =
    args.lockedTokens.length > 0
      ? [
          "",
          "Locked tokens — DO NOT include these in your output. The user has",
          "set them manually and they must be preserved:",
          ...args.lockedTokens.map((v) => `- ${v.key}: ${v.value}`),
        ].join("\n")
      : "";

  // In prompt mode the user's direction can also nudge the palette itself —
  // shifting hues, swapping a gradient, dialling a colour warmer/cooler.
  // Surface the unlocked palette entries as legal output keys when, and only
  // when, the user provided direction. In auto mode the palette is strictly
  // read-only.
  const userPromptText =
    args.mode === "prompt" && args.userPrompt && args.userPrompt.trim().length > 0
      ? args.userPrompt.trim()
      : "";
  const allowPaletteWriteback = userPromptText.length > 0;
  const paletteIsEmpty = paletteEntries.length === 0;
  const writebackParts: string[] = [];
  if (allowPaletteWriteback) {
    writebackParts.push("");
    if (paletteIsEmpty) {
      writebackParts.push(
        "The palette is currently EMPTY. Before filling the semantic tokens you",
        "MUST create palette entries that match the user's direction below.",
      );
    } else {
      writebackParts.push(
        "You MAY rewrite existing palette entries OR add new ones to honour the",
        "user's direction below (only when the direction explicitly calls for it",
        "— leave the palette alone otherwise).",
      );
    }
    writebackParts.push(
      "",
      "Adding NEW palette entries — pick 3–6 entries total for a fresh palette,",
      "fewer when the direction asks for a tight identity. Each new entry needs:",
      "  - color.<slug>           — kebab-case slug derived from an evocative",
      "                             name (e.g. `color.duvel-red`,",
      "                             `color.warm-cream`, `color.midnight-ink`).",
      "                             Value is a #RRGGBB lowercase hex for solid",
      "                             entries, or a valid CSS gradient string for",
      "                             gradient entries (linear-gradient(...) etc.).",
      "  - color.<slug>.weight    — a whole number 0–100 capturing that entry's",
      "                             share of the brand's visual presence. Weights",
      "                             across all enabled entries should sum to ~100;",
      "                             pick higher weights for dominant identity",
      "                             colours, lower weights for accents.",
      "Place these keys alongside token.* keys in the same `tokens` object.",
    );
    if (!paletteIsEmpty) {
      writebackParts.push(
        "",
        "Existing palette entries you MAY rewrite (solid → #RRGGBB; gradient →",
        "CSS gradient string):",
        ...args.unlockedPaletteColors.map((v) => `- ${v.key} (color)`),
        ...args.unlockedPaletteGradients.map(
          (v) => `- ${v.key} (gradient — current: ${v.value || "(unset)"})`,
        ),
      );
    }
  }
  const paletteWritebackSection = writebackParts.join("\n");

  const directionSection = userPromptText
    ? ["", "Additional direction from the user:", userPromptText].join("\n")
    : "";

  return [
    "You are filling design tokens for a UI design system from a brand colour palette.",
    "The palette is a dynamic, user-authored list — each entry has a kebab-case",
    "slug (`color.<slug>`), an evocative label, a `$type` of color or gradient,",
    "and Appostle extensions carrying role, weight, and optional pinned rank.",
    "Honour the metadata: roles describe intent, weights describe dominance,",
    "disabled (OFF) entries are off-limits.",
    "",
    "Brand palette (the user's intent — labels, roles, weights and pins all",
    "matter as hints):",
    palette.length > 0
      ? palette
      : "(empty — no palette entries exist yet; see the palette writeback section below)",
    ...metadataLegend,
    "",
    "Token key prefix glossary:",
    "- token.bg-*       — background surfaces (page, cards, modals, inverted)",
    "- token.fg-*       — foreground text and icons",
    "- token.accent-*   — interactive / brand colour and its states",
    "- token.border-*   — separator lines",
    "- token.status-*   — semantic status (success / warning / danger / info)",
    "  status-*-fg variants are the foreground text on the matching status surface",
    "",
    "Mapping rules (strong defaults — only deviate if the user direction below",
    "explicitly says otherwise):",
    "- token.bg-base ← the DOMINANT entry. That is the enabled entry tagged",
    "  role=neutral with the highest weight; failing that, the highest-weight",
    "  enabled entry overall.",
    "  Use its value directly when it's a solid hex, or pick an extremely near",
    "  tint (within ±3% lightness) of the same hue — never a different hue.",
    "  If the dominant entry is a gradient, derive a solid hex from one of",
    "  its colour stops (whichever reads as the calmest background).",
    "- token.bg-surface and token.bg-elevated are subtle siblings of bg-base",
    "  (same hue, slightly different lightness). Do not introduce a new hue.",
    "- token.bg-inverted ← the contrasting end of the dominance ladder: the",
    "  enabled entry whose luminance is furthest from bg-base.",
    "- token.fg-primary ← the colour used for text on bg-base; pick the enabled",
    "  entry with the strongest contrast against bg-base (typically the same",
    "  entry that backed bg-inverted).",
    "- token.accent-base ← the enabled entry tagged role=primary; failing that,",
    "  role=accent; failing that, the highest-weight non-neutral entry that is",
    "  not already backing the background. Hover/active are darker/lighter",
    "  shades of accent-base; subtle is a desaturated tint.",
    "- Status colours are independent of palette weight — pick standard hues",
    "  (green / amber / red / blue) that read clearly on bg-base.",
    "",
    "Tokens to fill (every value must be a #RRGGBB lowercase hex string):",
    tokensToFill,
    lockedSection,
    gradientSection,
    lockedGradientSection,
    paletteWritebackSection,
    directionSection,
    "",
    "Return JSON only with the shape:",
    '{ "tokens": { "<key>": "#rrggbb or css-gradient-string", ... } }',
    "",
    "Output ONLY the unlocked tokens, unlocked gradients, and (in prompt mode",
    "only, when the direction explicitly warrants it) the listed palette",
    "entries. Do not include locked entries, do not invent new keys, do not",
    "output anything outside the JSON. Never use a value from an entry marked OFF.",
  ]
    .filter((line) => line !== "")
    .join("\n");
}

export async function generateAndApplyBrandTokens(
  options: BrandTokenGenerationOptions,
): Promise<BrandTokenGenerationResult> {
  const { agentManager, workspaceRoot, brandPath, mode, prompt, logger, ownerUserId } = options;

  // ---------------------------------------------------------------------
  // Read the current colors.md to figure out what's palette, what's a
  // token, and which tokens are locked. Locked values are read from disk —
  // not from the request — so we always honour the latest file state.
  // ---------------------------------------------------------------------
  const brands = await listBrands({ workspaceRoot });
  const colorsBrand = brands.find((b) => b.name === "colors") ?? null;
  if (!colorsBrand) {
    throw new Error("Brand colors section not found — ensure tokens.json is present");
  }
  void brandPath; // API parameter preserved for backward compatibility

  const allVars = colorsBrand.variables;
  const paletteVars = allVars.filter((v) => v.key.startsWith("color."));
  const tokenVars = allVars.filter((v) => v.key.startsWith("token."));
  // `gradientVars` only holds non-palette gradient *tokens* (i.e. token.*
  // entries with type=gradient). Palette gradients live under `color.*` and
  // are handled by the separate palette-writeback acceptance set below so we
  // can constrain them to prompt mode.
  const gradientVars = allVars.filter((v) => v.type === "gradient" && v.key.startsWith("token."));
  // Palette entries the agent is allowed to rewrite (prompt mode only). The
  // ".weight" sidecars are metadata, not values the LLM ever owns — they're
  // already controlled by the UI balance cluster — so filter them out here.
  const palettePrimitives = paletteVars.filter(
    (v) => (v.type === "color" || v.type === "gradient") && !v.key.endsWith(".weight"),
  );
  const unlockedPaletteColors = palettePrimitives.filter(
    (v) => v.type === "color" && !v.locked && v.enabled !== false,
  );
  const unlockedPaletteGradients = palettePrimitives.filter(
    (v) => v.type === "gradient" && !v.locked && v.enabled !== false,
  );
  // `accent-on-dark` is derived deterministically below; the LLM never sees
  // it. Strip it from the prompt + acceptance sets here.
  const llmTokens = tokenVars.filter((v) => v.key !== DERIVED_ACCENT_ON_DARK);
  const unlockedTokens = llmTokens.filter((v) => !v.locked);
  const lockedTokens = llmTokens.filter((v) => v.locked);
  const unlockedGradients = gradientVars.filter((v) => !v.locked);
  const lockedGradients = gradientVars.filter((v) => v.locked);
  const accentOnDarkVar = allVars.find((v) => v.key === DERIVED_ACCENT_ON_DARK);

  // Roles aren't surfaced through BrandVariable yet — read them straight
  // from tokens.json so the prompt can describe each palette entry's intent.
  const paletteRoles = await readPaletteRoles(workspaceRoot);

  const accentOnDarkNeedsDerive = accentOnDarkVar !== undefined && !accentOnDarkVar.locked;
  if (unlockedTokens.length === 0 && unlockedGradients.length === 0 && !accentOnDarkNeedsDerive) {
    logger.info({ brandPath }, "brand-tokens: nothing to generate (all tokens locked)");
    return { generatedCount: 0 };
  }
  // An empty palette is no longer a hard error — in prompt mode the agent may
  // create palette entries from scratch (e.g. "fill based on this website",
  // "warm earth tones with a bold red accent"). Auto mode still needs at
  // least one palette entry to derive from, so we keep that guard scoped.
  if (paletteVars.length === 0 && mode === "auto") {
    throw new Error(
      "Brand palette is empty — type a direction (e.g. 'fill the palette') or add a colour first",
    );
  }

  // ---------------------------------------------------------------------
  // Spawn the internal agent and ask for filled token values. Skip the LLM
  // round-trip when the only thing left to fill is `accent-on-dark` (which
  // is derived deterministically below).
  // ---------------------------------------------------------------------
  const acceptedUpdates = new Map<string, string>();
  const needsLlmCall = unlockedTokens.length > 0 || unlockedGradients.length > 0;

  if (needsLlmCall) {
    const agentPrompt = buildPrompt({
      paletteVars,
      unlockedTokens,
      lockedTokens,
      unlockedGradients,
      lockedGradients,
      unlockedPaletteColors,
      unlockedPaletteGradients,
      paletteRoles,
      mode,
      userPrompt: prompt,
    });

    let response: { tokens: Record<string, string> };
    try {
      response = await generateStructuredAgentResponseWithFallback({
        manager: agentManager,
        cwd: workspaceRoot,
        prompt: agentPrompt,
        schema: TokensResponseSchema,
        schemaName: "BrandTokens",
        maxRetries: 2,
        providers: DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
        ownerUserId,
        agentConfigOverrides: {
          title: "Brand token generator",
          internal: true,
        },
      });
    } catch (error) {
      if (
        error instanceof StructuredAgentResponseError ||
        error instanceof StructuredAgentFallbackError
      ) {
        logger.warn({ err: error, brandPath }, "Structured brand token generation failed");
        throw new Error("Token generation failed — the agent did not return valid JSON");
      }
      throw error;
    }

    // -------------------------------------------------------------------
    // Apply only valid values for unlocked, known keys. Hex tokens must
    // match #RRGGBB; gradient keys accept CSS gradient strings. Anything
    // else is dropped — defence-in-depth so a misbehaving agent can never
    // overwrite a locked token or create a bogus key.
    //
    // Palette writeback (rewriting `color.<slug>` entries) is only honoured
    // in prompt mode with non-empty user direction. Auto mode keeps the
    // palette strictly read-only so a stray model output can't silently
    // mutate the brand's identity colours.
    // -------------------------------------------------------------------
    const unlockedTokenKeySet = new Set(unlockedTokens.map((v) => v.key));
    const unlockedGradientKeySet = new Set(unlockedGradients.map((v) => v.key));
    const allowPaletteWriteback = mode === "prompt" && (prompt?.trim().length ?? 0) > 0;
    const unlockedPaletteColorKeySet = allowPaletteWriteback
      ? new Set(unlockedPaletteColors.map((v) => v.key))
      : new Set<string>();
    const unlockedPaletteGradientKeySet = allowPaletteWriteback
      ? new Set(unlockedPaletteGradients.map((v) => v.key))
      : new Set<string>();
    // Existing palette slugs (any locked/unlocked, color or gradient) so we
    // know which `color.<slug>` keys would be net-new vs. an attempted
    // rewrite of an entry the user pinned — net-new entries are always
    // honoured in prompt mode; rewrites must hit the unlocked sets.
    const existingPaletteKeySet = new Set(palettePrimitives.map((v) => v.key));
    const PALETTE_SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;
    const isNewPaletteSlugKey = (key: string): boolean => {
      if (!key.startsWith("color.")) return false;
      const parts = key.split(".");
      if (parts.length !== 2) return false;
      const slug = parts[1];
      if (!slug || !PALETTE_SLUG_RE.test(slug)) return false;
      return !existingPaletteKeySet.has(key);
    };
    const isPaletteWeightKey = (key: string): boolean => {
      if (!key.startsWith("color.") || !key.endsWith(".weight")) return false;
      const slug = key.slice("color.".length, -".weight".length);
      return PALETTE_SLUG_RE.test(slug);
    };
    for (const [key, value] of Object.entries(response.tokens)) {
      if (typeof value !== "string") continue;
      const trimmed = value.trim();
      if (unlockedTokenKeySet.has(key) || unlockedPaletteColorKeySet.has(key)) {
        if (!HEX6.test(trimmed)) continue;
        acceptedUpdates.set(key, trimmed.toLowerCase());
      } else if (unlockedGradientKeySet.has(key) || unlockedPaletteGradientKeySet.has(key)) {
        if (!CSS_GRADIENT.test(trimmed)) continue;
        acceptedUpdates.set(key, trimmed);
      } else if (allowPaletteWriteback && isNewPaletteSlugKey(key)) {
        // Net-new palette entry — accept either a hex (solid) or a CSS
        // gradient string. The writer's `ensureColorPaletteEntry` upserts
        // the leaf node on disk based on the BrandVariable `type`.
        if (HEX6.test(trimmed)) {
          acceptedUpdates.set(key, trimmed.toLowerCase());
        } else if (CSS_GRADIENT.test(trimmed)) {
          acceptedUpdates.set(key, trimmed);
        }
      } else if (allowPaletteWriteback && isPaletteWeightKey(key)) {
        const n = Number(trimmed);
        if (Number.isFinite(n) && n >= 0 && n <= 100) {
          acceptedUpdates.set(key, String(Math.round(n)));
        }
      }
    }

    if (acceptedUpdates.size === 0 && !accentOnDarkNeedsDerive) {
      logger.warn(
        { brandPath, returnedKeys: Object.keys(response.tokens).length },
        "brand-tokens: agent returned no usable tokens",
      );
      return { generatedCount: 0 };
    }
  }

  // ---------------------------------------------------------------------
  // Derive `accent-on-dark` deterministically from the final accent-base
  // and bg-inverted values (post-merge with any LLM updates). The brand
  // contract guarantees this token reads at >= 4.5:1 on bg-inverted, so
  // builders can apply text-accent-on-dark on dark surfaces without
  // per-build judgment.
  // ---------------------------------------------------------------------
  if (accentOnDarkNeedsDerive) {
    const finalValueFor = (key: string): string | undefined => {
      const update = acceptedUpdates.get(key);
      if (update !== undefined) return update;
      const existing = allVars.find((v) => v.key === key);
      const v = existing?.value;
      return typeof v === "string" && v.length > 0 ? v : undefined;
    };
    const accentBase = finalValueFor("token.accent-base");
    const bgInverted = finalValueFor("token.bg-inverted");
    if (accentBase && bgInverted && HEX6.test(accentBase) && HEX6.test(bgInverted)) {
      const accentSubtleRaw = finalValueFor("token.accent-subtle");
      const accentSubtle =
        accentSubtleRaw && HEX6.test(accentSubtleRaw) ? accentSubtleRaw : undefined;
      const derived = deriveAccentOnDark({
        accent: accentBase,
        bgInverted,
        accentSubtle,
      });
      acceptedUpdates.set(DERIVED_ACCENT_ON_DARK, derived);
    }
  }

  if (acceptedUpdates.size === 0) {
    logger.warn({ brandPath }, "brand-tokens: no usable token updates produced");
    return { generatedCount: 0 };
  }

  // Build the next variables array — preserve everything as-is except for
  // the accepted unlocked-token updates. Translate adapter-emitted display
  // keys (e.g. token.bg-base) back to tokens.json tree paths (token.bg.base)
  // for the writer's dot-path navigation.
  const existingKeySet = new Set(allVars.map((v) => v.key));
  const nextVariables: BrandVariable[] = allVars.map((v) => {
    const update = acceptedUpdates.get(v.key);
    if (update === undefined) return v;
    return { ...v, key: adapterKeyToTokensJsonPath(v.key), value: update };
  });
  // Append net-new entries the LLM produced (palette colours/gradients and
  // their .weight siblings). Colours/gradients first, then weights — the
  // writer processes weight keys by looking up the parent node, so the
  // parent must precede its weight sibling in the iteration order.
  const newPaletteColorEntries: BrandVariable[] = [];
  const newPaletteWeightEntries: BrandVariable[] = [];
  for (const [key, value] of acceptedUpdates) {
    if (existingKeySet.has(key)) continue;
    if (key.endsWith(".weight") && key.startsWith("color.")) {
      newPaletteWeightEntries.push({
        key,
        type: "number",
        label: "",
        value,
      });
      continue;
    }
    if (key.startsWith("color.") && key.split(".").length === 2) {
      const isGradient = CSS_GRADIENT.test(value);
      newPaletteColorEntries.push({
        key,
        type: isGradient ? "gradient" : "color",
        label: "",
        value,
      });
    }
  }
  nextVariables.push(...newPaletteColorEntries, ...newPaletteWeightEntries);

  await writeBrandTokens(
    {
      description: colorsBrand.description,
      variables: nextVariables,
    },
    workspaceRoot,
  );

  logger.info(
    { brandPath, generatedCount: acceptedUpdates.size },
    "brand-tokens: applied generated values",
  );
  return { generatedCount: acceptedUpdates.size };
}
